import { Component, createSignal, onMount, createMemo, Show } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import LineChart from '../components/charts/LineChart';
import DataTable from '../components/tables/DataTable';
import DateRangePicker from '../components/ui/DateRangePicker';
import StatsCard from '../components/ui/StatsCard';
import { TbClick, TbCheckupList, TbCoin, TbMinus, TbPlus, TbChartLine } from 'solid-icons/tb';
import { statsApi } from '../services/api';
import { getTodayGMT } from '../utils/timezone';
import { config } from '../config/environment';
import moment from 'moment';

export interface NetworkPerformanceRow {
  user: string;
  visits: number;
  leads: number;
  cr: number;
  epc: number;
  earning: number;
}

const Report: Component = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = createSignal(true);
  const today = getTodayGMT();
  const [startDate, setStartDate] = createSignal(today);
  const [endDate, setEndDate] = createSignal(today);
  const [performanceData, setPerformanceData] = createSignal<NetworkPerformanceRow[]>([]);

  // Initialize with empty data - will be populated from API
  const [clicksData, setClicksData] = createSignal({
    labels: [] as string[],
    data: [] as number[],
  });

  const [conversionsData, setConversionsData] = createSignal({
    labels: [] as string[],
    data: [] as number[],
  });

  const [earningsData, setEarningsData] = createSignal({
    labels: [] as string[],
    data: [] as number[],
  });

  // Performance Overview - Minimized state with localStorage persistence
  const [performanceMinimized, setPerformanceMinimized] = createSignal(
    localStorage.getItem('report-performance-minimized') === 'true'
  );

  // Compute stats from real data
  const totalClicks = createMemo(() => {
    const data = clicksData().data;
    if (data.length === 0) return 0;
    return data.reduce((a, b) => a + b, 0);
  });

  const totalConversions = createMemo(() => {
    const data = conversionsData().data;
    if (data.length === 0) return 0;
    return data.reduce((a, b) => a + b, 0);
  });

  const totalEarnings = createMemo(() => {
    const data = earningsData().data;
    if (data.length === 0) return 0;
    return data.reduce((a, b) => a + b, 0);
  });

  // Check if date range spans 2 or more different days
  const isMultiDayRange = createMemo(() => {
    const start = moment(startDate());
    const end = moment(endDate());
    return end.diff(start, 'days') >= 1;
  });

  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
    loadData();
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const clicksChart = await statsApi.getClicksChart('day', startDate(), endDate());
      const conversionsChart = await statsApi.getConversionsChart('day', startDate(), endDate());
      const reportData = await statsApi.getReport(startDate(), endDate());

      if (clicksChart) {
        setClicksData({
          labels: clicksChart.labels,
          data: clicksChart.datasets[0]?.data || [],
        });
      }

      if (conversionsChart) {
        setConversionsData({
          labels: conversionsChart.labels,
          data: conversionsChart.datasets[0]?.data || [],
        });

        // Extract earnings data from conversions chart (second dataset)
        if (conversionsChart.datasets[1]) {
          setEarningsData({
            labels: conversionsChart.labels,
            data: conversionsChart.datasets[1]?.data || [],
          });
        }
      }

      if (reportData && reportData.length > 0) {
        const mappedData = reportData.map((item: any) => ({
          user: item.user,
          visits: item.clicks,
          leads: item.conversions,
          cr: parseFloat(item.cr) || 0,
          epc: parseFloat(item.epc) || 0,
          earning: parseFloat(item.earning) || 0,
        }));
        setPerformanceData(mappedData);
      }
    } catch (error) {
      // Error loading data
    } finally {
      setLoading(false);
    }
  };

  onMount(() => {
    loadData();
  });

  return (
    <div class="space-y-6">
        {/* Header */}
        <div>
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Report</h1>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Analyze trends, performance metrics and network publisher performance
          </p>
        </div>

        {/* Stats */}
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatsCard
            title="Total Clicks"
            value={totalClicks().toString()}
            icon={() => <TbClick size={24} />}
            color="blue"
            loading={loading()}
          />
          <StatsCard
            title="Total Conversions"
            value={totalConversions().toString()}
            icon={() => <TbCheckupList size={24} />}
            color="green"
            loading={loading()}
          />
          <StatsCard
            title="Total Earning"
            value={`$${totalEarnings().toFixed(2)}`}
            icon={() => <TbCoin size={24} />}
            color="yellow"
            loading={loading()}
          />
        </div>

        {/* Date Filter */}
        <DateRangePicker
          onDateChange={handleDateChange}
          initialStartDate={startDate()}
          initialEndDate={endDate()}
        />

        {/* Charts */}
        <Show when={isMultiDayRange()}>
          <div class={`bg-white dark:bg-gray-800 rounded-lg shadow transition-all duration-300 ${performanceMinimized() ? 'p-2 opacity-50' : 'p-6'}`}>
            <div class={`flex items-center justify-between ${performanceMinimized() ? 'px-1' : 'mb-4'}`}>
              <h2 class={`font-semibold flex items-center gap-2 ${performanceMinimized() ? 'text-xs text-gray-500 dark:text-gray-500' : 'text-lg text-gray-900 dark:text-white'}`}>
                <TbChartLine size={performanceMinimized() ? 16 : 20} class={performanceMinimized() ? 'text-gray-500 dark:text-gray-500' : ''} />
                <span class={performanceMinimized() ? 'hidden' : ''}>Performance Overview</span>
              </h2>
              <button
                onClick={() => {
                  const newState = !performanceMinimized();
                  setPerformanceMinimized(newState);
                  localStorage.setItem('report-performance-minimized', String(newState));
                }}
                class="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition text-gray-500 dark:text-gray-400 flex-shrink-0"
                title={performanceMinimized() ? 'Expand' : 'Collapse'}
              >
                {performanceMinimized() ? <TbPlus size={20} /> : <TbMinus size={20} />}
              </button>
            </div>
            <Show when={!performanceMinimized()}>
              <LineChart
                labels={clicksData().labels}
                datasets={[
                  {
                    label: 'Clicks',
                    data: clicksData().data,
                    borderColor: 'rgb(99, 102, 241)',
                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                    yAxisID: 'y',
                  },
                  {
                    label: 'Conversions',
                    data: conversionsData().data,
                    borderColor: 'rgb(16, 185, 129)',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    yAxisID: 'y1',
                  },
                  {
                    label: 'Earning',
                    data: earningsData().data,
                    borderColor: 'rgb(245, 158, 11)',
                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                    yAxisID: 'y2',
                  },
                ]}
                height={300}
              />
            </Show>
          </div>
        </Show>

        {/* Network Performance Table */}
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div class="p-6 border-b border-dashed border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
              Network Performance
            </h2>
          </div>
          <DataTable<NetworkPerformanceRow>
            data={performanceData()}
            columns={[
              {
                key: 'user',
                label: 'User',
                sortable: true,
                render: config.features.enableUserDetails
                  ? (item) => (
                      <button
                        onClick={() => navigate(`/report/user/${encodeURIComponent(item.user)}`)}
                        class="text-gray-900 dark:text-gray-100 hover:text-amber-600 dark:hover:text-amber-400 hover:underline font-bold transition-colors"
                      >
                        {item.user}
                      </button>
                    )
                  : undefined,
              },
              {
                key: 'visits',
                label: 'Visits',
                sortable: true,
                render: (item) => item.visits.toLocaleString(),
              },
              {
                key: 'leads',
                label: 'Leads',
                sortable: true,
                render: (item) => item.leads.toLocaleString(),
              },
              {
                key: 'cr',
                label: 'CR',
                sortable: true,
                render: (item) => `${item.cr.toFixed(2)} %`,
              },
              {
                key: 'epc',
                label: 'EPC',
                sortable: true,
                render: (item) => `${item.epc.toFixed(2)}`,
              },
              {
                key: 'earning',
                label: 'Earning',
                sortable: true,
                render: (item) => (
                  <div class="flex items-center gap-1">
                    <span>{item.earning.toFixed(2)}</span>
                    <span class="text-orange-500">💰</span>
                  </div>
                ),
              },
            ]}
            loading={loading()}
            searchable={true}
            searchPlaceholder="Search by User..."
          />
        </div>
    </div>
  );
};

export default Report;
